import {
  switchMap,
  map,
  withLatestFrom,
  filter,
  mapTo,
  catchError
} from 'rxjs/operators'
import { from, of } from 'rxjs'
import { ofType } from 'redux-observable'
import Actions from '../actions'
import { getPayload } from '../utils/rxUtils'
import { calculateTotalPrice } from '../utils/paymentUtils'
import $t from '../i18n'
import { routes } from '../consts'
import {
  isHaveSubscription,
  isHaveCoupon,
  isHaveCredit,
  requestedPaymentMethodChange,
  isVisitPriceZero,
  isZuoraSubscription,
  isVisitsRemaining
} from '../utils/paymentUtils'
import {
  getJSON,
  getPDF,
  isLoggedIn,
  postJSON,
  putJSON
} from '../utils/fetchUtils'
import {
  catchAndCancelActions,
  translateErrorMsg
} from '../utils/customErrorHandler'
import validateZipcode from '../utils/validateZipcode'
import {
  serializeHmoUnautenticatedPaymentDetails,
  serializePaymentDetails
} from '../utils/serializerUtil'
import { downloadPdf } from '../utils/fileUtils'
import { getIsHmoMember } from '../utils/hmoUtils'

export const slotSavedTimerEnd = action$ =>
  action$.pipe(
    ofType(Actions.SLOT_EXPIRED),
    mapTo(Actions.createAction(Actions.ENABLE_SLOT_EXPIRE_DIALOG))
  )

export const autoSelectPlan = action$ =>
  action$.pipe(
    getPayload(Actions.SUBSCRIPTION_PLANS_RECEIVED),
    filter(({ plans }) => plans.length === 1),
    switchMap(({ plans }) =>
      of(
        Actions.createAction(Actions.PAYMENT_PLAN_SELECTED_FROM_PLANS, {
          selectedPlan: plans[0]
        })
      )
    )
  )

// ******************************** THE PAYMENT FLOW *********************************
// if there is credit -> the user alredy paid: popup usedCredit -> appointment set (CONFIRMED)
// if no credit and no subscription -> set appointment and go to plans (UNCONFIRMED)
// if no credit and do have subscription:
//                      1. if coupon (first visit free): popup usedCoupon -> appointment set (CONFIRMED)
//                      2. if no coupon : popup approve payment -> appointment set (CONFIRMED)

// Subscribed users sould approved discount payment
export const approveSubscribedPaymentEpicDynamic = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.PAYMENT_FLOW_TRIGGERED),
    withLatestFrom(state$),
    switchMap(([, state]) => {
      // if alredy paid (subscription + no subscription)
      if (isHaveCredit(state)) {
        return from([
          Actions.createAction(Actions.CREDIT_REDEEMED),
          Actions.createAction(Actions.PAYMENT_FLOW_COMPLETED)
        ])
      }

      if (isHaveSubscription(state)) {
        if (isZuoraSubscription(state)) {
          if (isVisitsRemaining(state)) {
            return from([Actions.createAction(Actions.PAYMENT_FLOW_COMPLETED)])
          } else {
            return createApprovePaymentDialogDynamic()
          }
        }
        // first visit
        if (isHaveCoupon(state)) {
          // AlreadyPaidPopup('couponUsed')
          return from([
            Actions.createAction(Actions.CREDIT_REDEEMED),
            Actions.createAction(Actions.PAYMENT_FLOW_COMPLETED)
          ])
        }
        if (isVisitPriceZero(state)) {
          return from([Actions.createAction(Actions.PAYMENT_FLOW_COMPLETED)])
        }
        // pay for discounted visit
        else {
          return createApprovePaymentDialogDynamic()
        }
      }

      // no subscription and no credit - unapproved appointment is made
      return of(Actions.createAction(Actions.PAYMENT_FLOW_COMPLETED))
    }),
    filter(payment => payment)
  )

const createApprovePaymentDialogDynamic = () => {
  return of(Actions.createAction(Actions.SHOW_APPROVE_PAYMENT_POP_UP))
}

export const paymentMethodUpdated = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.PAYMENT_METHOD_UPDATED),
    withLatestFrom(state$),
    map(([, state]) => {
      if (requestedPaymentMethodChange(state)) {
        return Actions.createAction(Actions.APPOINTMENT_TIMEWINDOWS_REQUESTED)
      }
    }),
    filter(payment => payment)
  )

export const paymentFinished = actions$ =>
  actions$.pipe(
    getPayload(Actions.PAYMENT_SUCCESS),
    // filter out unauthenticated users
    filter(() => isLoggedIn()),
    switchMap(() =>
      of(
        Actions.createAction(Actions.CLEAR_ZOURA_SIGNATURES),
        Actions.createAction(Actions.NOTIFICATIONS_REQUESTED),
        Actions.createAction(Actions.APPOINTMENTS_ETA_REQUESTED),
        Actions.createAction(Actions.APPOINTMENTS_POLL_REQUESTED)
      )
    )
  )

export const unauthenticatedPaymentFinished = actions$ =>
  actions$.pipe(
    getPayload(Actions.PAYMENT_SUCCESS),
    // only unauthenticated users
    filter(() => !isLoggedIn()),
    switchMap(() => of(Actions.createAction(Actions.CLEAR_ZOURA_SIGNATURES)))
  )

export const proceedToPaymentNoCoupon = actions$ =>
  actions$.pipe(
    getPayload(Actions.PROCEED_TO_CHECKOUT_PRESSED),
    filter(({ customerCoupon }) => !customerCoupon),
    switchMap(payload =>
      from([
        Actions.createAction(Actions.REDIRECTED_TO_CHECKOUT_PAGE, payload),
        Actions.createAction(Actions.RESET_COUPON_ZUORA)
      ])
    )
  )
export const proceedToPaymentCouponInserted = actions$ =>
  actions$.pipe(
    getPayload(Actions.PROCEED_TO_CHECKOUT_PRESSED),
    filter(({ customerCoupon }) => customerCoupon),
    switchMap(payload =>
      of(Actions.createAction(Actions.IS_COUPON_VALID_REQUESTED, payload))
    )
  )
export const fullCouponValidZuora = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.IS_COUPON_VALID_RECEIVED),
    withLatestFrom(state$),
    filter(([, state]) => state.app.features.billing_enabled),
    filter(([{ data }]) => data.give_full_discount),
    switchMap(() =>
      of(
        Actions.createAction(
          Actions.IS_COUPON_VALID_RECEIVED_SKIP_CHECKOUT_PAGE
        )
      )
    )
  )

export const fullCouponValidSkipCheckoutWithAddress = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.IS_COUPON_SKIP_CHECKOUT_ADDRESS_UPDATE_RECEIVED),
    withLatestFrom(state$),
    switchMap(([, state]) => {
      const { selectedPlan } = state.payment
      const userState = validateZipcode(state.auth.locationVerification.zipcode)
      const isBlackListedState =
        selectedPlan?.blackListedStates?.includes(userState)
      if (userState && isBlackListedState) {
        return of(
          Actions.createAction(Actions.LOCATION_VERIFICATION_FILLED),
          Actions.createAction(Actions.SHOW_NO_SUBSCRIPTIONS_MODAL)
        )
      }
      return of(
        Actions.createAction(Actions.USER_PROFILE_REQUESTED),
        Actions.createAction(Actions.REDIRECT_TO_PAYMENT_SUCCESS),
        Actions.createAction(Actions.PAYMENT_SUCCESS),
        Actions.createAction(Actions.PROCEED_TO_CHECKOUT_PRESSED_CANCELLED)
      )
    })
  )

const checkIfStatePresent = state => {
  return state.auth.locationVerification.state === null
}

export const discountCouponValidZuora = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.IS_COUPON_VALID_RECEIVED),
    withLatestFrom(state$),
    filter(
      ([, state]) =>
        state.app.features.billing_enabled && !checkIfStatePresent(state)
    ),
    filter(([{ data }]) => !data.give_full_discount),
    switchMap(([payload]) =>
      of(Actions.createAction(Actions.REDIRECTED_TO_CHECKOUT_PAGE, payload))
    )
  )

export const requestUpdateAddress = action$ =>
  action$.pipe(
    getPayload(Actions.IS_COUPON_SKIP_CHECKOUT_ADDRESS_UPDATE_REQUESTED),
    switchMap(({ address, city, zipcode, state }) => {
      return postJSON(`/device/address_update`, {
        address,
        city,
        zipcode,
        state
      }).pipe(
        switchMap(() =>
          of(
            Actions.createAction(
              Actions.IS_COUPON_SKIP_CHECKOUT_ADDRESS_UPDATE_RECEIVED
            ),
            Actions.createAction(Actions.PAYMENT_SUCCESS),
            Actions.createAction(Actions.APPOINTMENTS_POLL_REQUESTED),
            Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
              eventName:
                Actions.IS_COUPON_SKIP_CHECKOUT_ADDRESS_UPDATE_REQUESTED +
                '_WEB'
            })
          )
        ),
        catchError(e => {
          return of(
            Actions.createAction(
              Actions.IS_COUPON_SKIP_STRIPE_ADDRESS_UPDATE_FAILED,
              translateErrorMsg(e)
            )
          )
        })
      )
    })
  )

export const proceedWithPaymentSuccess = actions$ =>
  actions$.pipe(
    getPayload(Actions.PAYMENT_SUCCESS),
    switchMap(() =>
      of(Actions.createAction(Actions.REDIRECT_TO_PAYMENT_SUCCESS))
    )
  )

export const redirectToPaymentPage = actions$ =>
  actions$.pipe(
    getPayload(Actions.CHECKOUT_SESSION_RECEIVED_STRIPE),
    mapTo(Actions.createAction(Actions.NAVIGATED_TO, routes.PAYMENT_PAGE))
  )

export const logAnalyticsForPendingHTNFUAppointment = (actions$, state$) =>
  actions$.pipe(
    ofType(Actions.REQUEST_CANCEL_PENDING_HTNFU_APPOINTMENT),
    withLatestFrom(state$),
    switchMap(([, state]) => {
      const { antidote_clinic, is_clinic_follow_up } =
        state.appointments.pendingAppointment
      const havePendingHTNFUAppointment =
        antidote_clinic === 'hypertension' && is_clinic_follow_up

      const slotExpiredPopUpAction = Actions.createAction(
        Actions.SLOT_EXPIRED_POPUP_SHOWED
      )

      const htnfuAppointmentCancelAction = Actions.createAction(
        Actions.ANALYTICS_EVENT_FIRED,
        {
          eventName: Actions.HTN_FOLLOW_UP_CANCELLED_PAYMENT_FAILED
        }
      )

      if (havePendingHTNFUAppointment) {
        return of(htnfuAppointmentCancelAction, slotExpiredPopUpAction)
      } else {
        return of(slotExpiredPopUpAction)
      }
    })
  )

export const getPaymentDetailsIfPremiumBilling = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.USER_PROFILE_RECEIVED),
    withLatestFrom(state$),
    filter(
      ([, state]) =>
        getIsHmoMember(state.profile.userDetails) ||
        // for non HMO member, we need to fetch payment details
        // if they have subscription
        isHaveSubscription(state)
    ),
    mapTo(Actions.createAction(Actions.PAYMENT_METHOD_DATA_REQUESTED))
  )

// Zoura Payment Details Page related calls
// Calculate total price whenever payment config changes
export const updateTotalPrice = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.UPDATE_PAYMENT_CONFIG_SUBSCRIPTION),
    withLatestFrom(state$),
    map(([, state]) => {
      const { balancePerSubscription, paymentConfigPerSubscriptions } =
        state.payment
      const total = calculateTotalPrice(
        balancePerSubscription,
        paymentConfigPerSubscriptions
      )
      return Actions.createAction(Actions.UPDATE_TOTAL_PAYMENT_PRICE, total)
    })
  )

export const requestPaymentMethodData = action$ =>
  action$.pipe(
    getPayload(Actions.PAYMENT_METHOD_DATA_REQUESTED),
    switchMap(() => {
      return getJSON('/zuora/payment-details/').pipe(
        switchMap(data => {
          const serializedData = serializePaymentDetails(data)
          return of(
            Actions.createAction(Actions.PAYMENT_METHOD_DATA_RECIEVED, {
              data: serializedData
            }),
            Actions.createAction(Actions.BALANCE_PER_SUBSCRIPTION_RECIEVED, {
              data: data?.balancePerSubscription || []
            })
          )
        }),
        catchError(() => {
          return of(Actions.createAction(Actions.PAYMENT_METHOD_FETCH_FAILED))
        })
      )
    })
  )

export const requestPaymentCardUpdateSignature = action$ =>
  action$.pipe(
    getPayload(Actions.GENERATE_UPDATE_PAYMENT_CARD_REQUESTED),
    switchMap(() => {
      return getJSON(`/zuora/payment-method-signature/`).pipe(
        map(data =>
          Actions.createAction(
            Actions.GENERATE_UPDATE_PAYMENT_CARD_RECEIVED,
            data
          )
        )
      )
    })
  )

export const updatePaymentMethodCard = action$ =>
  action$.pipe(
    getPayload(Actions.PAYMENT_METHOD_CARD_UPDATE_REQUESTED),
    switchMap(data => {
      const {
        defaultPaymentMethodId,
        skipSuccessMessage,
        creditCardAddress1,
        creditCardAddress2,
        creditCardCity,
        creditCardState,
        creditCardPostalCode,
        ...rest
      } = data

      const payload = {
        ...rest,
        defaultPaymentMethodId,
        billToContact: {
          address1: creditCardAddress1,
          address2: creditCardAddress2,
          city: creditCardCity,
          state: creditCardState,
          zipCode: creditCardPostalCode
        }
      }

      return putJSON(`/zuora/payment-details/`, payload).pipe(
        switchMap(() =>
          of(
            Actions.createAction(Actions.PAYMENT_METHOD_CARD_UPDATED),
            ...(skipSuccessMessage
              ? []
              : [
                  Actions.createAction(Actions.MESSAGE_DISPLAYED, {
                    content: $t('pages.paymentDetails.updateSuccess'),
                    type: 'success'
                  })
                ]),
            Actions.createAction(Actions.PAYMENT_METHOD_DATA_REQUESTED)
          )
        ),
        catchAndCancelActions([
          Actions.createAction(Actions.PAYMENT_METHOD_CARD_FAILED)
        ])
      )
    })
  )

export const confirmAutoPayClickedEpic = action$ =>
  action$.pipe(
    ofType(Actions.CONFIRM_AUTO_PAY_CLICKED),
    map(() =>
      Actions.createAction(Actions.SET_AUTO_PAY_REQUESTED, {
        autoPay: true,
        setNewPaymentMethod: false
      })
    )
  )

export const setAutoPayEpic = action$ =>
  action$.pipe(
    getPayload(Actions.SET_AUTO_PAY_REQUESTED),
    switchMap(
      ({ autoPay, setNewPaymentMethod, paymentMethodId, paymentMethodType }) =>
        postJSON('/zuora/hmo-auto-pay-payment-method/', {
          autoPay,
          setNewPaymentMethod,
          paymentMethodId,
          paymentMethodType
        }).pipe(
          switchMap(() =>
            of(
              Actions.createAction(Actions.SET_AUTO_PAY_RECEIVED),
              Actions.createAction(Actions.MESSAGE_DISPLAYED, {
                content: autoPay
                  ? $t('dialogs.autoPay.autopaySetSuccessfully')
                  : $t('dialogs.autoPay.autopayDisabledSuccessfully'),
                type: 'success'
              }),
              Actions.createAction(Actions.PAYMENT_METHOD_DATA_REQUESTED),
              Actions.createAction(Actions.NAVIGATED_TO, routes.MANAGE_PAYMENTS)
            )
          ),
          catchAndCancelActions([
            Actions.createAction(Actions.SET_AUTO_PAY_FAILED)
          ])
        )
    )
  )

export const paymentSuccessPopMessage = action$ =>
  action$.pipe(
    getPayload(Actions.PAYMENT_SUCCESS, Actions.PAYMENT_SUCCESS_FROM_PPF),
    mapTo(
      Actions.createAction(Actions.MESSAGE_DISPLAYED, {
        content: $t('alerts.paymentCompleted'),
        type: 'success'
      })
    )
  )

export const requestPaymentInvoiceEpic = action$ =>
  action$.pipe(
    getPayload(Actions.PAYMENT_INVOICE_REQUESTED),
    switchMap(({ invoiceId, invoiceFile }) => {
      return getPDF(invoiceFile?.replace('/v1', '')).pipe(
        switchMap(result => {
          downloadPdf(result, `invoice-${invoiceId}`)
          return of(Actions.createAction(Actions.PAYMENT_INVOICE_RECEIVED))
        }),
        catchError(() => {
          return of(Actions.createAction(Actions.PAYMENT_INVOICE_FAILED))
        })
      )
    })
  )

export const requestUnauthenticatedPaymentInfoEpic = action$ =>
  action$.pipe(
    getPayload(Actions.UNAUTENTICATED_PAYMENT_DETAILS_REQUESTED),
    switchMap(({ memberId, subscriptionId, token, transactionId }) => {
      return postJSON(`/zuora/hmo-payment-pages/`, {
        memberId,
        subscriptionId,
        token,
        transactionId
      }).pipe(
        switchMap(result => {
          const serializeResult =
            serializeHmoUnautenticatedPaymentDetails(result)
          const actions = [
            Actions.createAction(
              Actions.UNAUTENTICATED_PAYMENT_DETAILS_RECEIVED,
              {
                ...serializeResult,
                // if we have transaction id, we should deeplink to signup page with those params (sign up from cms)
                isFullyPaidOrSubsidized: result.balance?.totalPrice === 0,
                deepLink: transactionId
                  ? `/signup?memberId=${memberId}&subscriptionId=${subscriptionId}&transactionId=${transactionId}`
                  : serializeResult?.deepLink && token // if we have token, we should add it to the deep link
                  ? `${serializeResult?.deepLink}token=${token}`
                  : serializeResult?.deepLink
              }
            ),
            Actions.createAction(Actions.BALANCE_PER_SUBSCRIPTION_RECIEVED, {
              data: result?.balancePerSubscription || []
            })
          ]
          if (result.balance?.totalPrice === 0) {
            actions.push(
              Actions.createAction(Actions.REDIRECT_TO_PAYMENT_SUCCESS)
            )
          }

          return from(actions)
        }),
        catchError(() => {
          return of(
            Actions.createAction(Actions.UNAUTENTICATED_PAYMENT_DETAILS_FAILED)
          )
        })
      )
    })
  )

export const showAutopayOptionAfterPayment = action$ =>
  action$.pipe(
    getPayload(Actions.PAYMENT_SUCCESS),
    withLatestFrom(action$.pipe(getPayload(Actions.CREATE_ORDER_REQUESTED))),
    filter(
      ([, orderActionParams]) =>
        orderActionParams?.isHMOMember &&
        !orderActionParams?.autoPay &&
        isLoggedIn()
    ),
    mapTo(Actions.createAction(Actions.DISPLAY_AUTO_PAY_POPUP))
  )

export const setAutpayEpic = action$ =>
  action$.pipe(
    getPayload(Actions.SET_AUTO_PAY_REQUESTED),
    withLatestFrom(action$.pipe(getPayload(Actions.CREATE_ORDER_REQUESTED))),
    switchMap(([payload, orderActionParams]) => {
      // take the payment method id from the last order action params if it is not present in the payload
      const paymentMethodId =
        payload?.paymentMethodId || orderActionParams?.paymentMethodId
      const paymentMethodType =
        payload?.paymentMethodType || orderActionParams?.paymentMethodType
      return postJSON('/zuora/hmo-auto-pay-payment-method/', {
        paymentMethodId,
        paymentMethodType,
        setNewPaymentMethod: true,
        autoPay: true
      }).pipe(
        switchMap(() =>
          of(
            Actions.createAction(Actions.SET_AUTO_PAY_RECEIVED),
            Actions.createAction(Actions.CLOSE_AUTO_PAY_POPUP),
            Actions.createAction(Actions.MESSAGE_DISPLAYED, {
              content: $t('dialogs.autoPay.autopaySetSuccessfully'),
              type: 'success'
            })
          )
        ),
        catchError(() => {
          return of(
            Actions.createAction(Actions.SET_AUTO_PAY_FAILED),
            Actions.createAction(Actions.CLOSE_AUTO_PAY_POPUP),
            Actions.createAction(Actions.MESSAGE_DISPLAYED, {
              content: $t('dialogs.autoPay.autopaySetError'),
              type: 'error'
            })
          )
        })
      )
    })
  )
